/**
 * 文本元素：占位符、默认尺寸、统一字号（全页面一致，不按画布宽度断点缩放）
 */

import { getBackgroundToneFromCss } from '@/components/canvas/shared/utils/color/backgroundTone';
import type { PageBackground } from '@/components/canvas/document/publicTypes';
import { isTextDocumentEmpty } from '@/components/canvas/elements/text/model/textPlain';
import {
  isHeadingBlockKind,
  TEXT_BLOCK_KINDS,
  type TextBlockKind,
} from '@/components/canvas/elements/text/utils/textContent';

export type { TextBlockKind } from '@/components/canvas/elements/text/utils/textContent';
export { TEXT_BLOCK_KINDS };

// ─── 文字颜色 ─────────────────────────────────────────────────────────────────

/** 浅色页面背景下的默认文字颜色（深色字） */
export const LIGHT_MODE_FONT_COLOR = '#1a1a1a';

/** 深色页面背景下的默认文字颜色（浅色字） */
export const DARK_MODE_FONT_COLOR = '#f0f0f0';

/**
 * 注脚颜色：在正文颜色基础上叠加 60% 透明度（hex 8 位 = 0x99）
 * 0.6 × 255 = 153 = 0x99
 */
export const LIGHT_MODE_FOOTNOTE_COLOR = '#1a1a1a99';
export const DARK_MODE_FOOTNOTE_COLOR  = '#f0f0f099';

/** 根据背景推断颜色的通用逻辑 */
function resolveSample(bg: PageBackground): string | null {
  if (bg.type === 'color' && bg.value) return bg.value;
  if (bg.type === 'gradient' && bg.value) {
    const hex = bg.value.match(/#(?:[0-9a-f]{3,8})\b/i);
    const rgb = !hex && bg.value.match(/rgba?\([^)]+\)/i);
    return hex ? hex[0] : rgb ? rgb[0] : null;
  }
  if ((bg.type === 'image' || bg.type === 'transparent') && bg.backgroundColor) {
    return bg.backgroundColor;
  }
  return null;
}

/**
 * 根据画布背景推断合适的文字颜色。
 */
export function getFontColorForBackground(bg: PageBackground): string {
  const sample = resolveSample(bg);
  if (!sample) return LIGHT_MODE_FONT_COLOR;
  const tone = getBackgroundToneFromCss(sample);
  return tone === 'dark' ? DARK_MODE_FONT_COLOR : LIGHT_MODE_FONT_COLOR;
}

/** 根据画布背景推断合适的注脚颜色 */
export function getFootnoteColorForBackground(bg: PageBackground): string {
  const sample = resolveSample(bg);
  if (!sample) return LIGHT_MODE_FOOTNOTE_COLOR;
  const tone = getBackgroundToneFromCss(sample);
  return tone === 'dark' ? DARK_MODE_FOOTNOTE_COLOR : LIGHT_MODE_FOOTNOTE_COLOR;
}

// ─── 文本占位符 ─────────────────────────────────────────────────────────────

/** 单击插入标题（h1） */
export const TEXT_PLACEHOLDER_H1 = '输入标题';
/** 拖拽选区 / 段落 / 列表 */
export const TEXT_PLACEHOLDER_BODY = '输入内容';

export const TEXT_KNOWN_PLACEHOLDERS = new Set([
  TEXT_PLACEHOLDER_H1,
  TEXT_PLACEHOLDER_BODY,
  '大标题',
  '副标题',
  '正文',
  '请输入内容',
  '单击编辑文本',
]);

export function getTextPlaceholderForBlockKind(blockKind: TextBlockKind): string {
  return isHeadingBlockKind(blockKind) ? TEXT_PLACEHOLDER_H1 : TEXT_PLACEHOLDER_BODY;
}

export function getTextPlaceholder(blockKind: TextBlockKind): string {
  return getTextPlaceholderForBlockKind(blockKind);
}

export function isPlaceholderText(textEl: {
  content?: string;
  document?: Parameters<typeof isTextDocumentEmpty>[0];
}): boolean {
  if (textEl.document && isTextDocumentEmpty(textEl.document)) return true;
  if (!textEl.content?.trim()) {
    return true;
  }
  if (textEl.content && TEXT_KNOWN_PLACEHOLDERS.has(textEl.content)) return true;
  return false;
}

// ─── 插入文本元素时各类型的默认尺寸与字重 ────────────────────────────────────

/** 文本元素的默认行高倍数 */
export const TEXT_ELEMENT_DEFAULT_LINE_HEIGHT = 1.4;

export const TEXT_BLOCK_LAYOUT_DEFAULTS: Record<
  TextBlockKind,
  { width: number; lines: number; fontWeight: number }
> = {
  h1: { width: 400, lines: 1, fontWeight: 700 },
  h2: { width: 360, lines: 1, fontWeight: 600 },
  h3: { width: 300, lines: 1, fontWeight: 600 },
  paragraph: { width: 200, lines: 1, fontWeight: 400 },
  'unordered-list-item': { width: 400, lines: 1, fontWeight: 400 },
  'ordered-list-item': { width: 400, lines: 1, fontWeight: 400 },
};

export function computeBlockLayoutHeight(blockKind: TextBlockKind, fontSize: number): number {
  const { lines } = TEXT_BLOCK_LAYOUT_DEFAULTS[blockKind];
  return Math.ceil(fontSize * TEXT_ELEMENT_DEFAULT_LINE_HEIGHT * lines);
}

/** 文本元素默认内边距（px） */
export const TEXT_ELEMENT_DEFAULT_PADDING = 0;

// ─── 统一字号（h1–h6 / 正文）────────────────────────────────────────────────

export interface TextFontSizes {
  h1: number;
  h2: number;
  h3: number;
  body: number;
}

/** 画布文本全页面统一字号（px） */
export const TEXT_FONT_SIZES: TextFontSizes = {
  h1: 64,
  h2: 48,
  h3: 32,
  body: 32,
};

export function getTextFontSizes(): TextFontSizes {
  return TEXT_FONT_SIZES;
}

export function getFontSizeForBlockKind(blockKind: TextBlockKind): number {
  const sizes = TEXT_FONT_SIZES;
  if (blockKind === 'paragraph' || blockKind === 'ordered-list-item' || blockKind === 'unordered-list-item') {
    return sizes.body;
  }
  return sizes[blockKind];
}

export function getBlockKindDisplayName(blockKind: TextBlockKind): string {
  const map: Record<TextBlockKind, string> = {
    h1: '标题 1',
    h2: '标题 2',
    h3: '标题 3',
    paragraph: '段落',
    'unordered-list-item': '无序列表',
    'ordered-list-item': '有序列表',
  };
  return map[blockKind];
}
